import { compact, last, uniqBy } from 'lodash'
import type { FlowGroup, FlowGroupUnit, FlowItem, ViewablePageReport } from './types.no-deps'
import { map } from '../../lib/functional/array.no-deps'
import type { Dictionary } from '../../lib/functional/dictionary.no-deps'
import { pipe } from '../../lib/functional/pipe.no-deps'
import { fromLoopPath } from '../flows/LoopPath.no-deps'
import type * as Guide from '../guides'
import type { AppUnitType, KnownUnit } from '../units/types.no-deps'
import { type CompactReportGetOutput, isLegacyReport, type Answer } from '../reports/types.no-deps'
import { findById } from '../../lib/functional/identifiable.no-deps'
import type { Element, ElementId, Flow, FlowSummary } from '../flows/types.no-deps'
import type { Nullable, UUID } from '../../lib/typeHelpers.no-deps'
import { findElement } from '../flows/element.no-deps'
import { isNil, isNotNil } from '../../lib/functional/nil.no-deps'

export function answerToViewable(answer: Answer, photoUrls: Dictionary<string>) {
    const { label, value, timestamp, unitTypeIds, dynamicElement } = answer

    const viewableValue = { ...value }
    if (Array.isArray(viewableValue.value) && viewableValue.type === 'photos') {
        viewableValue.value = viewableValue.value.map((key) => photoUrls[key])
    }

    return { label, value: viewableValue, timestamp: timestamp.toString(), unitTypeIds, dynamicElement }
}

export function buildFlowGroups(
    elementSummary: Nullable<FlowSummary['elements']>,
    photoUrls: Dictionary<string>,
    answers: Answer[],
    flow: Flow,
    loopUnits: FlowGroupUnit[],
    includeTextContentElements: boolean = false
): FlowGroup[] {
    // All items to be sorted into groups. We will accept any element type at
    // this point to help sorting them with the answers, but will filter out
    // unwanted ones later
    const flowItems: Array<FlowItem<Element>> = []

    // Find and add all elements from the flow summary
    if (elementSummary) {
        for (const { elementId, loopPath } of elementSummary) {
            const element = findElement(flow, elementId)
            if (isNotNil(element)) {
                flowItems.push({ type: 'element', element, loopPath })
            }
        }
    }

    // Because not all answers have elements and vice-versa, we make a best
    // guess at interleaving them
    {
        let insertIndex = 0
        for (const answer of answers) {
            if (isNil(answer.loopPath) || isNotNil(answer.activityIds)) {
                continue
            }

            const item: FlowItem = {
                type: 'answer',
                answer: answerToViewable(answer, photoUrls),
                loopPath: answer.loopPath,
            }

            // Either insert the answer after the element it references, or
            // just place it at the current index
            const newInsertIndex = flowItems.findIndex(
                (item, index) =>
                    answer.value.type !== 'calculation' &&
                    item.type === 'element' &&
                    item.loopPath === answer.loopPath &&
                    item.element.id === answer.value.rawAnswer.element &&
                    index >= insertIndex
            )
            insertIndex = newInsertIndex === -1 ? insertIndex : newInsertIndex

            flowItems.splice(insertIndex, 0, item)
            insertIndex += 1
        }
    }

    const flowGroups: FlowGroup[] = []
    // Construct groups from the items we have collated
    for (const item of flowItems) {
        // Currently we only use answers and text content elements in reports
        if (item.type === 'element' && (!includeTextContentElements || item.element.type !== 'text-content')) {
            continue
        }

        // If the latest group has a different loop path to the current item
        // or we havn't created a group yet, create a new group for subsequent
        // items to be added to
        let flowGroup = last(flowGroups)
        if (flowGroup?.loopPath !== item.loopPath) {
            const units: FlowGroupUnit[] = pipe(
                fromLoopPath(item.loopPath),
                map((loopId) => {
                    if (loopId.unitId == null) return null

                    const unit = findById(loopUnits, loopId.unitId)
                    if (unit == null) return null

                    // We only need the id, name and image of the unit to satisfy type FlowGroupUnit
                    const flowGroupUnit: FlowGroupUnit = {
                        id: unit.id,
                        name: unit.name,
                        image: unit.image ?? undefined,
                    }

                    return flowGroupUnit
                }),
                compact
            )

            flowGroup = { type: 'FlowGroup', loopPath: item.loopPath, items: [], units }
            flowGroups.push(flowGroup)
        }

        flowGroup.items.push(item as FlowItem)
    }

    return flowGroups
}

export function buildViewablePageReport({
    report,
    answers,
    title,
    flow,
    guideLookup,
    unitsInAnswers,
    unitTypesInAnswers,
    subjectUnit,
    loopUnits,
    ancestorReports,
    includeTextContentElements,
}: {
    report: CompactReportGetOutput
    answers: Answer[]
    title: string
    flow: Flow
    guideLookup: Dictionary<Guide.SyncGuide>
    unitsInAnswers: KnownUnit[]
    unitTypesInAnswers: AppUnitType[]
    subjectUnit: KnownUnit
    loopUnits: FlowGroupUnit[]
    ancestorReports?: UUID[]
    includeTextContentElements?: boolean
}): ViewablePageReport {
    const isLegacy = isLegacyReport(report)

    // Group all the answers
    const viewableReportData: ViewablePageReport = {
        ...(report.originId && { originId: report.originId }),
        title,
        isLegacy,
        unitsInAnswers,
        unitTypesInAnswers,
        activityGroups: [],
        flowGroups: [],
        answers: [],
        subjectUnit,
        reportDetails: report.reportDetails,
        photoUrls: report.photoUrls,
        guideLookup,
        flow: flow ?? null,
        language: report.language,
        workflow: { id: report.workflow, version: report.workflowVersion },
        ...(ancestorReports && { ancestorReports }),
        cachedUnitData: report.cachedUnitData,
    }

    const reportAnswers: Answer[] = answers || []

    for (const answer of reportAnswers) {
        if (isNil(answer.loopPath)) {
            viewableReportData.answers.push(answerToViewable(answer, report.photoUrls))
        }
    }

    viewableReportData.flowGroups = buildFlowGroups(
        report.flowSummary?.elements,
        report.photoUrls,
        reportAnswers,
        flow,
        loopUnits,
        includeTextContentElements
    )

    return viewableReportData
}

export function getLoopUnitsInFlowGroups(flowGroups: FlowGroup[]): FlowGroupUnit[] {
    return uniqBy(
        flowGroups.flatMap((group) => group.units),
        'id'
    )
}

export const elementIdReferencedByItem = (item: FlowItem) => {
    if (item.type === 'element') {
        return item.element.id
    }
    if (item.answer.value.type !== 'calculation') {
        return item.answer.value.rawAnswer?.element
    }
    return null
}

export const elementIsReferencedByItem = (item: FlowItem, elementId: ElementId) =>
    elementIdReferencedByItem(item) === elementId

export function flowGroupsReferenceElement(flowGroups: FlowGroup[], elementId: ElementId) {
    return elementIdsReferencedByFlowGroups(flowGroups).some((id) => id === elementId)
}

export function elementIdsReferencedByFlowGroups(flowGroups: FlowGroup[]): ElementId[] {
    return flowGroups
        .map(({ items }) => items)
        .flat()
        .map(elementIdReferencedByItem)
        .filter(isNotNil)
}
